/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import { tableV2StackedCellContentSx } from '../../../components/v2/tableV2Styles';
import * as actions from '../../../store/jornada/actions';
import * as Roles from '../../../constants/role';
import * as selectorsAuth from '../../../store/auth/reducer';
import { VINICIUS_ADMIN_EMAIL } from 'utils';
import { formatWithDay } from 'utils/date-format';
import { jornadaListUrl, readJornadaFormId } from '../../../helpers/jornadaHelpers';
import { applyPeriodoInspetorQuery } from '../../../helpers/periodoInspetorQuery';
import Formulario from './Formulario';

const LIST_PATH = '/v2/jornada-medicao';

const getParceiroNome = (row) =>
  row?.inspetorAuxiliarNome || row?.inspetorAuxiliarName || row?.inspetorAuxiliar?.nome || '';

const usuarioParceiroGridSx = {
  '& .MuiDataGrid-cell[data-field="inspetorNome"]': {
    whiteSpace: 'normal',
    overflow: 'hidden',
    alignItems: 'center',
    py: 0.5,
  },
};

const getDefaultFilter = () => ({
  inicio: null,
  fim: null,
  tipoControle: 'CONTROLE_JORNADA',
});

const JornadaMedicaoV2 = ({
  authData,
  exportJornadas,
  deleteJornada,
  downloadJornadas,
  history,
  location,
}) => {
  const [openDownload, setOpenDownload] = useState(false);
  const [localFilter, setLocalFilter] = useState(() =>
    applyPeriodoInspetorQuery(getDefaultFilter(), location.search, { idKey: 'usuarioId' }));
  const [openExport, setOpenExport] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);

  const { open, formId } = readJornadaFormId(location.search);

  const defaultFilterMemo = useMemo(() => getDefaultFilter(), []);

  const apiFilter = useMemo(
    () => ({ ...localFilter, tipoControle: 'CONTROLE_JORNADA' }),
    [localFilter],
  );

  const isAdmin = () =>
    authData && (authData.role === Roles.ADMIN || authData.role === Roles.SUPER_ADMIN);

  const onView = (rowId) => {
    history.push(jornadaListUrl(LIST_PATH, location.search, rowId));
  };

  const onClose = () => {
    history.push(jornadaListUrl(LIST_PATH, location.search, null));
  };

  const headerActions = [
    {
      label: 'Exportar',
      onClick: () => setOpenExport(true),
      startIcon: <FileDownloadOutlinedIcon />,
      variant: 'outlined',
    },
  ];

  if (authData?.email === VINICIUS_ADMIN_EMAIL) {
    headerActions.push({
      label: 'Download Zip',
      onClick: () => setOpenDownload(true),
      startIcon: <FolderZipOutlinedIcon />,
      variant: 'outlined',
    });
  }

  const rowActions = [
    {
      key: 'view',
      icon: <VisibilityOutlinedIcon fontSize="small" />,
      title: 'Visualizar',
      onClick: (rowId) => onView(rowId),
      show: () => true,
    },
  ];

  const columns = [
    {
      field: 'data',
      headerName: 'Data',
      flex: 0.5,
      renderCell: (params) => formatWithDay(params.row.data),
    },
    { field: 'tipoServicoNome', headerName: 'Tipo de Serviço', flex: 0.7 },
    { field: 'cidade', headerName: 'Cidade', flex: 0.5 },
    {
      field: 'inspetorNome',
      headerName: 'Usuário e Parceiro',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => {
        const parceiroNome = getParceiroNome(params.row);
        return (
          <Box sx={tableV2StackedCellContentSx}>
            <Typography variant="body2" noWrap>
              <Box component="span" sx={{ fontWeight: 600 }}>Usuário: </Box>
              {params.row.inspetorNome}
            </Typography>
            {parceiroNome ? (
              <Typography variant="body2" noWrap>
                <Box component="span" sx={{ fontWeight: 600 }}>Parceiro: </Box>
                {parceiroNome}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
    { field: 'jornadaTotal', headerName: 'Total da Jornada', flex: 0.5 },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Jornada Medição V2</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="jornadas/filter"
          paginationUrl="/v2/jornada-medicao"
          filterData={apiFilter}
          refreshKey={reload}
          columns={columns}
          rowHeight={56}
          getRowHeight={(params) => (getParceiroNome(params.model) ? 72 : 56)}
          gridSx={usuarioParceiroGridSx}
          actions={rowActions}
          onDelete={
            isAdmin()
              ? (rowId) => deleteJornada(rowId, () => setReload((c) => !c))
              : undefined
          }
          headerActions={headerActions}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilterMemo}
          appliedFilter={localFilter}
          fields={[
            { field: 'useDates' },
            {
              field: 'useUsuarios',
              md: 12,
              xs: 12,
              multiSelect: true,
              grupos: [Roles.ADMIN, Roles.INSPETOR],
            },
            { field: 'useTipoServico', md: 6, xs: 12 },
            { field: 'useCodigo', codigoLabel: 'Cidade', md: 6, xs: 12 },
          ]}
          onFilter={(filterData) => {
            const next = { ...filterData, tipoControle: 'CONTROLE_JORNADA' };
            setLocalFilter(next);
            setOpenFilter(false);
          }}
        />

        <Formulario id={formId} open={open} onClose={onClose} />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => exportJornadas(apiFilter, () => setOpenExport(false))}
          title="Confirmação"
          confirmMessage="Exportar"
        >
          <DialogContentText>
            Deseja exportar todas as informações filtradas?
          </DialogContentText>
        </DialogDefaultV2>

        <DialogDefaultV2
          open={openDownload}
          handlerCancel={() => setOpenDownload(false)}
          handlerConfirm={() => {
            downloadJornadas(apiFilter, () => setOpenDownload(false));
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>
            Deseja baixar todos os arquivos das jornadas filtradas?
          </DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

JornadaMedicaoV2.propTypes = {
  authData: PropTypes.object,
  exportJornadas: PropTypes.func.isRequired,
  deleteJornada: PropTypes.func.isRequired,
  downloadJornadas: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...actions,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(JornadaMedicaoV2));
